import React from "react";
import { Icon, iconName } from "./icons/Icons";
import { Flex, FlexItem } from "./compositeComponents/FlexHelpers";
import { H2, H3, H4 } from "./typography/Heading";
import { TextAvatar } from "./avatar/TextAvatar";
import { Button, ButtonProps } from "./button/Button";
import { Small } from "./typography/Body";
import styled from "styled-components";
import { colors } from "./colors/color";
import type { PageSectionVariant } from './PageSectionDefs';
import { PropsWithChildren } from 'react';
import { PopupMenuButton, PopupMenuButtonProps } from "./compositeComponents/PopupMenuButton";

export type PageSectionHeaderProps = {
    icon?:iconName,
    styleVariant?:PageSectionVariant,
    headerVariant?:'H2' | 'H3' | 'H4',
    subTitle?:{
        text:string,
        icon?:iconName,
        iconColor?:string,
    },
    avatar?:{
        text:string,
    },
    button?:ButtonProps,
    popupMenuButton?:PopupMenuButtonProps,
    defaultCollapsed?:boolean,
    onCollapseChanged?:(collapsed:boolean)=>void,
    style?:React.CSSProperties
}

export function PageSectionHeader(props:PropsWithChildren<PageSectionHeaderProps>):JSX.Element
{
    const [collapsed, setCollapsed] = React.useState(
        props.defaultCollapsed===undefined ? false : props.defaultCollapsed
    );

    React.useEffect(()=>{
        if(props.defaultCollapsed !== undefined && props.defaultCollapsed !== collapsed)
            setCollapsed(props.defaultCollapsed);
    },[props.defaultCollapsed]);

    return (
        <Container styleVariant={props.styleVariant || 'default'} style={props.style}>
            <Flex justifyContent="space-between">
                
                <Flex flexDirection='column' grow='1' gap={2}>
                    
                    <Flex alignItems='center' justifyContent='space-between' gap={8}>
                        
                        <Flex alignItems='center' gap={8} grow='1'>

                            {props.icon && 
                                <FlexItem flexGrow='0' className="page-section-header-main-icon">
                                    <Icon name={props.icon} />
                                </FlexItem>
                            }

                            <FlexItem>
                                    {(props.headerVariant === undefined || props.headerVariant === 'H2') && <H2>{props.children}</H2>}
                                    {props.headerVariant === 'H3' && <H3>{props.children}</H3>}
                                    {props.headerVariant === 'H4' && <H4>{props.children}</H4>}
                            </FlexItem>

                            {props.avatar &&
                                <FlexItem flexGrow='0'>
                                    <TextAvatar text={props.avatar.text} />
                                </FlexItem>
                            }
                            
                            {props.defaultCollapsed !== undefined &&
                                <Button 
                                    className="page-section-header-button"
                                    variant='text'
                                    icon={collapsed ? 'arrows_chevron_chevronDown' : 'arrows_chevron_chevronUp'}
                                    onClick={() => {
                                        setCollapsed(!collapsed);
                                        props.onCollapseChanged?.(!collapsed);
                                    }} />
                            }
                        </Flex>

                        {props.button &&
                            <div style={{flexGrow:0}}>
                                <Button {...props.button} />
                            </div>
                        }
                        {props.popupMenuButton &&
                            <div style={{flexGrow:0}}>
                                <PopupMenuButton {...props.popupMenuButton} />
                            </div>
                        }

                    </Flex>


                    {props.subTitle &&
                        <Flex marginLeft={24} gap={4} alignItems='center'>
                            {props.subTitle.icon &&
                                <span className="page-section-header-icon">
                                    <Icon
                                        name={props.subTitle.icon}
                                        color={props.subTitle.iconColor}
                                        size="x-small"
                                    />
                                </span>
                            }
                            <Small>{props.subTitle.text}</Small>
                        </Flex>
                    }
                </Flex>

            </Flex>
        </Container>
    );
}


const getBackgroundColor = (styleVariant:PageSectionVariant) =>
{
    if(styleVariant === 'dark' || styleVariant === 'dark-card') return colors.pageSection.header.variant.dark;
    return colors.pageSection.header.variant.default;
}

const getForegroundColor = (styleVariant:PageSectionVariant) =>
{
    if(styleVariant === 'dark' || styleVariant === 'dark-card') return colors.text.white;
    return colors.text.normal;
}

const Container = styled.div<{ styleVariant: PageSectionVariant }>`
    padding: 4px 8px;
    background-color: ${props => getBackgroundColor(props.styleVariant)};
    color: ${props => getForegroundColor(props.styleVariant)};
    h2, h3, h4 {
        color: ${props => getForegroundColor(props.styleVariant)};
        font-size: 16px;
        font-weight: 600;
    };

    .page-section-header-main-icon svg {
        color: ${props => getForegroundColor(props.styleVariant)};
        fill: ${props => getForegroundColor(props.styleVariant)};
    }

    .page-section-header-button {
        color: ${props => getForegroundColor(props.styleVariant)};
    }
`;